import { useState, useEffect, useMemo } from 'react';
import { getSignataires } from '../services/operationMsService';
import { SignataireSelection } from '../domain/analyse/types';

export interface SignataireOption {
  label: string;
  value: string; // `${id}|${type_signataire}` (composite, scinde au moment de l'envoi)
}

// Charge les signataires d'un organisme et AUTO-SELECTIONNE (comportement legacy) : le
// signataire deja designe sur l'operation s'il existe, sinon le premier de la liste. Ainsi
// un acheteur signataire n'a pas a choisir. Le choix reste modifiable (sans alert).
export const useSignataires = (
  organismeId?: number,
  designatedId?: number,
  designatedType?: string,
) => {
  const [signataires, setSignataires] = useState<SignataireOption[]>([]);
  const [selectedSignataireId, setSelectedSignataireId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!organismeId) return undefined;
    let active = true;
    setLoading(true);
    getSignataires(organismeId)
      .then((res) => {
        if (!active) return;
        const list = res.data ?? [];
        setSignataires(
          list.map((s) => ({
            label: `${s.prenom ?? ''} ${s.nom ?? ''} (${s.type_signataire ?? ''})`.trim(),
            value: `${s.id}|${s.type_signataire ?? ''}`,
          })),
        );
        // Auto-selection : signataire designe de l'operation, sinon premier.
        setSelectedSignataireId((prev) => {
          if (prev) return prev;
          const match =
            designatedId !== undefined
              ? list.find((s) => s.id === designatedId && s.type_signataire === designatedType)
              : undefined;
          const chosen = match ?? list[0];
          return chosen ? `${chosen.id}|${chosen.type_signataire ?? ''}` : null;
        });
      })
      .catch(() => {
        if (active) setSignataires([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [organismeId, designatedId, designatedType]);

  const selection = useMemo<SignataireSelection>(() => {
    if (!selectedSignataireId) return {};
    const [id, type_signataire] = selectedSignataireId.split('|');
    return { id_signataire: Number(id), type_signataire };
  }, [selectedSignataireId]);

  return { signataires, selectedSignataireId, setSelectedSignataireId, selection, loading };
};
